<template>
  <div class="mobile-questionnaire-page">
    <div
      class="questionnaire-content"
      v-if="questionnaireTemplate"
      :class="{ 'has-fixed-button': !isScrolledToBottom }"
    >
      <div class="questionnaire-header">
        <h2>职业健康调查问卷</h2>

        <!-- ✅ 显眼提示：问卷做完后系统才会提交考卷 -->
        <div class="questionnaire-warning">
          <span class="warning-icon">⚠</span>
          <span class="warning-text"
            >重要提示：问卷做完后系统才会提交考卷，请务必完成所有问题。</span
          >
        </div>

        <div class="questionnaire-intro">
          <p>您好！</p>
          <p>
            我们邀请您参加一项职业健康状况问卷调查，本问卷调查旨在了解您的职业心理、身体活动水平、肌肉骨骼疾患的基本情况，为职业人群的健康保护提供依据。我们保证对您提供的个人信息资料进行保密，并承诺所有资料仅用于学术研究。谢谢您的合作！
          </p>
          <p>广东省职业健康协会</p>
        </div>
      </div>

      <div class="questionnaire-questions">
        <template v-for="question in visibleQuestions" :key="question.id">
          <!-- Likert复合题 -->
          <LikertCompositeQuestion
            v-if="question.type === 'LIKERT_COMPOSITE'"
            :ref="(el) => setQuestionRef(question.id, el)"
            :question-id="question.id"
            :display-index="question.displayIndex"
            :title="question.title"
            :required="question.required"
            :has-error="questionErrors.has(question.id)"
            :likert-config="question.compositeConfig.likert.scaleConfig"
            :sub-questions="question.compositeConfig.likert.subQuestions"
            :model-value="answers"
            @update:modelValue="handleAnswersUpdate"
          />

          <!-- 其他题型（基础题型、矩阵题、疼痛评分题等） -->
          <QuestionnaireItem
            v-else
            :ref="(el) => setQuestionRef(question.id, el)"
            :question="question"
            :current-index="question.displayIndex"
            :total-count="viewQuestions.length"
            :show-progress="true"
            :all-answers="answers"
            :has-error="questionErrors.has(question.id)"
            v-model="answers[question.id]"
          />
        </template>
      </div>

      <!-- 提交按钮（在内容流中的原始位置） -->
      <div
        v-if="isScrolledToBottom"
        class="submit-section submit-btn relative-position"
      >
        <van-button
          type="primary"
          block
          :loading="submitting"
          @click="handleSubmitClick"
          class="submit-btn-inner"
          :class="{
            'has-errors': !canSubmit && !submitting,
          }"
        >
          提交问卷
        </van-button>
      </div>
    </div>

    <!-- 提交按钮（固定定位，悬浮在底部） -->
    <div
      v-if="questionnaireTemplate && !isScrolledToBottom"
      class="submit-section submit-btn fixed-bottom"
    >
      <van-button
        type="primary"
        block
        :loading="submitting"
        @click="handleSubmitClick"
        class="submit-btn-inner"
        :class="{
          'has-errors': !canSubmit && !submitting,
        }"
      >
        提交问卷
      </van-button>
    </div>

    <div v-if="!questionnaireTemplate" class="loading-container">
      <van-loading type="spinner" vertical>加载问卷模板中...</van-loading>
    </div>
  </div>
</template>

<script>
import QuestionnaireItem from "@/components/QuestionnaireItem.vue";
import LikertCompositeQuestion from "@/components/LikertCompositeQuestion.vue";
import { get, post } from "@/request.js";
import {
  transformBackendToView,
  calculateVisibleQuestions,
} from "@/utils/questionnaireTransformer.js";
import { getStorage, setStorage, removeStorage } from "@/assets/js/storage.js";

export default {
  name: "MobileQuestionnaire",
  components: {
    QuestionnaireItem,
    LikertCompositeQuestion,
  },
  props: {
    examId: {
      type: Number,
      required: true,
    },
    templateCode: {
      type: String,
      default: null,
    },
  },
  emits: ["submit-success", "go-back"],
  data() {
    return {
      questionnaireTemplate: null,
      viewQuestions: [], // 转换后的题目列表
      answers: {},
      submitting: false,
      saveCacheTimer: null, // 防抖定时器
      questionRefs: new Map(), // 题目组件的 ref 引用
      questionErrors: new Set(), // 有错误的题目ID集合
      isScrolledToBottom: false, // 是否滚动到底部
      scrollCheckTimer: null, // 滚动检测防抖定时器
      scrollCheckLocked: false, // 滚动检测锁定标志（防止按钮切换时的抖动）
    };
  },
  computed: {
    sortedViewQuestions() {
      // 按 displayIndex 排序
      return [...this.viewQuestions].sort(
        (a, b) => a.displayIndex - b.displayIndex
      );
    },
    // 根据答案和跳转逻辑计算可见题目列表
    visibleQuestions() {
      if (!this.sortedViewQuestions || this.sortedViewQuestions.length === 0) {
        return [];
      }
      return calculateVisibleQuestions(this.sortedViewQuestions, this.answers);
    },
    // 获取用户ID（用于缓存key）
    studentId() {
      try {
        const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
        return userInfo.id || null;
      } catch (e) {
        return null;
      }
    },
    // 生成缓存key：含 questionnaireEffectiveRevision，模板更新或本场冻结后与旧草稿隔离
    cacheKey() {
      if (!this.examId || !this.studentId) {
        return null;
      }
      // 优先使用 props 中的 templateCode，如果没有则从 questionnaireTemplate 获取
      const templateCode =
        this.templateCode || this.questionnaireTemplate?.templateCode;
      if (!templateCode) {
        return null;
      }
      const rev = this.questionnaireTemplate?.questionnaireEffectiveRevision;
      const revPart = rev != null && rev !== undefined ? String(rev) : "0";
      return `questionnaire_draft_${this.examId}_${this.studentId}_${templateCode}_${revPart}`;
    },
    canSubmit() {
      if (!this.visibleQuestions || this.visibleQuestions.length === 0) {
        console.log("[问卷调试] canSubmit: visibleQuestions 为空");
        return false;
      }

      const result = this.visibleQuestions.every((question) => {
        if (!question.required) {
          return true;
        }

        // Likert复合题的答案存储在 answers[question.id] 对象中
        if (question.type === "LIKERT_COMPOSITE") {
          const compositeAnswer = this.answers[question.id];
          if (!compositeAnswer || typeof compositeAnswer !== "object") {
            console.log("[问卷调试] canSubmit: Likert复合题未填写", {
              questionId: question.id,
              compositeAnswer: compositeAnswer,
            });
            return false;
          }
          // 检查所有子题是否都有答案
          const allSubQuestionsAnswered =
            question.compositeConfig.likert.subQuestions.every((subQ) => {
              const subAnswer = compositeAnswer[subQ.id];
              return subAnswer !== null && subAnswer !== undefined;
            });
          if (!allSubQuestionsAnswered) {
            console.log("[问卷调试] canSubmit: Likert复合题子问题未全部填写", {
              questionId: question.id,
              compositeAnswer: compositeAnswer,
            });
          }
          return allSubQuestionsAnswered;
        }

        // 疼痛评分题特殊处理（与 PC 端对齐）
        // 是否题必须填写
        if (question.painRatingRelated?.isYesNoQuestion) {
          const answer = this.answers[question.id];
          const isValid = answer !== null && answer !== undefined;
          if (!isValid) {
            console.log("[问卷调试] canSubmit: 疼痛是否题未填写", {
              questionId: question.id,
              answer: answer,
            });
          }
          return isValid;
        }
        // 评分题：如果关联的是否题选"是"，则必须填写；选"否"可以不填
        if (
          question.type === "RATING" &&
          question.painRatingRelated?.isRatingQuestion
        ) {
          const yesNoQuestionId = question.painRatingRelated.yesNoQuestionId;
          const yesNoAnswer = this.answers[yesNoQuestionId];
          if (!yesNoAnswer || yesNoAnswer === "no") {
            return true;
          }
          const ratingAnswer = this.answers[question.id];
          const isValid = ratingAnswer !== null && ratingAnswer !== undefined;
          if (!isValid) {
            console.log(
              '[问卷调试] canSubmit: 疼痛评分题未填写（是否题选"是"）',
              {
                questionId: question.id,
                yesNoAnswer: yesNoAnswer,
                ratingAnswer: ratingAnswer,
              }
            );
          }
          return isValid;
        }

        // 矩阵题特殊处理
        if (question.type === "MATRIX") {
          const matrixAnswer = this.answers[question.id];
          if (
            !matrixAnswer ||
            typeof matrixAnswer !== "object" ||
            Object.keys(matrixAnswer).length === 0
          ) {
            console.log("[问卷调试] canSubmit: 矩阵题未填写", {
              questionId: question.id,
              questionType: question.type,
              answer: matrixAnswer,
            });
            return false;
          }
          // 检查矩阵题的所有行是否都已填写
          const allRowsAnswered = question.matrixConfig?.rows?.every((row) => {
            return (
              matrixAnswer[row.id] !== null &&
              matrixAnswer[row.id] !== undefined
            );
          });
          if (!allRowsAnswered) {
            console.log("[问卷调试] canSubmit: 矩阵题未全部填写", {
              questionId: question.id,
              questionType: question.type,
              answer: matrixAnswer,
            });
            return false;
          }
          return true;
        }

        const answer = this.answers[question.id];
        if (answer === null || answer === undefined) {
          console.log("[问卷调试] canSubmit: 必填题未填写", {
            questionId: question.id,
            questionType: question.type,
            answer: answer,
          });
          return false;
        }

        if (typeof answer === "string" && answer.trim() === "") {
          console.log("[问卷调试] canSubmit: 必填题答案为空字符串", {
            questionId: question.id,
            questionType: question.type,
            answer: answer,
          });
          return false;
        }

        if (Array.isArray(answer) && answer.length === 0) {
          console.log("[问卷调试] canSubmit: 必填多选题未选择", {
            questionId: question.id,
            questionType: question.type,
            answer: answer,
          });
          return false;
        }

        return true;
      });

      // 详细记录每个必填题目的状态
      const requiredQuestionsStatus = this.viewQuestions
        .filter((q) => q.required)
        .map((q) => ({
          id: q.id,
          type: q.type,
          title: q.title?.substring(0, 20) || "",
          hasAnswer: this.answers.hasOwnProperty(q.id),
          answer: this.answers[q.id],
          isValid: (() => {
            if (q.type === "LIKERT_COMPOSITE") {
              const compositeAnswers = this.answers[q.id];
              if (!compositeAnswers || typeof compositeAnswers !== "object") {
                return false;
              }
              return q.compositeConfig.likert.subQuestions.every((subQ) => {
                const subAnswer = compositeAnswers[subQ.id];
                return subAnswer !== null && subAnswer !== undefined;
              });
            }
            const answer = this.answers[q.id];
            if (answer === null || answer === undefined) return false;
            if (typeof answer === "string" && answer.trim() === "")
              return false;
            if (Array.isArray(answer) && answer.length === 0) return false;
            return true;
          })(),
        }));

      console.log("[问卷调试] canSubmit 结果:", {
        result: result,
        viewQuestionsCount: this.viewQuestions.length,
        requiredQuestionsCount: this.viewQuestions.filter((q) => q.required)
          .length,
        answersKeys: Object.keys(this.answers),
        requiredQuestionsStatus: requiredQuestionsStatus,
      });

      return result;
    },
  },
  mounted() {
    this.loadQuestionnaireTemplate();
    // 添加滚动监听（使用防抖优化）
    this.handleScroll = this.debouncedCheckScrollPosition.bind(this);
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    // 初始检查一次（延迟检查，确保DOM已渲染）
    this.isScrolledToBottom = false;
    this.$nextTick(() => {
      setTimeout(() => {
        this.checkScrollPosition();
      }, 500);
    });
  },
  beforeUnmount() {
    // 组件销毁时清除防抖定时器
    if (this.saveCacheTimer) {
      clearTimeout(this.saveCacheTimer);
      this.saveCacheTimer = null;
    }
    if (this.scrollCheckTimer) {
      clearTimeout(this.scrollCheckTimer);
      this.scrollCheckTimer = null;
    }
    // 移除滚动监听
    if (this.handleScroll) {
      window.removeEventListener("scroll", this.handleScroll);
    }
  },
  watch: {
    // 监听 answers 变化，自动保存到 localStorage（防抖处理）
    answers: {
      handler(newAnswers, oldAnswers) {
        this.saveAnswersToCache(newAnswers);
        // 当用户填写答案后，自动清除该题目的错误状态
        this.clearErrorsForAnsweredQuestions(newAnswers);

        // 检查跳转逻辑，如果触发跳转，滚动到目标题目
        this.$nextTick(() => {
          this.handleSkipLogic(newAnswers, oldAnswers);
        });
      },
      deep: true,
    },
  },
  methods: {
    async loadQuestionnaireTemplate() {
      try {
        const res = await get(`/api/my/exam/${this.examId}/questionnaire`);
        if (res.success && res.data) {
          this.questionnaireTemplate = res.data;

          // 数据转换：后端模型 → 前端渲染模型
          this.viewQuestions = transformBackendToView(
            this.questionnaireTemplate.questions
          );

          // 初始化答案对象（Vue 3 中直接赋值即可，响应式系统会自动处理）
          this.questionnaireTemplate.questions.forEach((question) => {
            if (question.type === "MULTIPLE_CHOICE") {
              this.answers[question.id] = [];
            } else if (
              question.type === "LIKERT_SCALE" &&
              question.parentQuestionId
            ) {
              // Likert子题的答案存储在复合题的 answers 对象中
              // 初始化时不需要单独处理，由 LikertCompositeQuestion 组件管理
            } else if (question.type === "MATRIX") {
              // 矩阵题：答案格式为 {rowId: columnValue}，初始化为空对象
              this.answers[question.id] = {};
            } else if (
              question.type === "SINGLE_CHOICE" &&
              question.options &&
              question.options.some(
                (opt) => opt.value === "no" || opt.label === "否"
              ) &&
              question.options.some(
                (opt) => opt.value === "yes" || opt.label === "是"
              )
            ) {
              // 疼痛是否题：暂时不选，默认为null
              this.answers[question.id] = null;
            } else if (question.type === "TEXT") {
              this.answers[question.id] = ""; // TEXT 类型初始化为空字符串
            } else {
              this.answers[question.id] = null;
            }
          });

          // 初始化 Likert 复合题的答案对象（空对象）
          this.viewQuestions
            .filter((q) => q.type === "LIKERT_COMPOSITE")
            .forEach((composite) => {
              if (!this.answers[composite.id]) {
                this.answers[composite.id] = {};
              }
              // 为每个子问题初始化 null
              composite.compositeConfig.likert.subQuestions.forEach((subQ) => {
                if (this.answers[composite.id][subQ.id] === undefined) {
                  this.answers[composite.id][subQ.id] = null;
                }
              });
            });

          // 尝试从 localStorage 恢复已保存的答案（断点续填）
          // 注意：必须在答案初始化完成后才能恢复缓存
          this.$nextTick(() => {
            this.restoreAnswersFromCache();
          });
        } else {
          this.$toast.fail(res.message || "获取问卷模板失败");
          console.log("[问卷调试] 加载模板失败，通知父组件关闭问卷壳", res);
          this.clearAnswersCache();
          this.$emit("go-back", { questionnaireLoadFailed: true });
        }
      } catch (error) {
        console.error("加载问卷模板失败:", error);
        this.$toast.fail("加载问卷模板失败，请稍后重试");
        console.log("[问卷调试] 加载模板异常，通知父组件关闭问卷壳", error);
        this.clearAnswersCache();
        this.$emit("go-back", { questionnaireLoadFailed: true });
      }
    },

    /**
     * 处理答案更新（用于 Likert 复合题）
     */
    handleAnswersUpdate(newAnswers) {
      this.answers = newAnswers;
    },

    /**
     * 转换答案格式为后端期望的格式
     * Likert复合题的答案需要展开为子问题答案
     */
    transformAnswersForBackend(frontendAnswers) {
      // 按 viewQuestions 逐题构建后端答案，避免全量拷贝再删除导致冲突 ID 答案丢失
      // 问题场景：q44 同时是 q43 的子题（答案存在 frontendAnswers["q43"]["q44"]）
      // 和另一 Likert 组的父 ID（frontendAnswers["q44"] 是整个组的对象）。
      // 旧方案先拷贝再 delete backendAnswers["q44"] 会把子题答案一起删掉。
      const backendAnswers = {};

      this.viewQuestions.forEach((q) => {
        if (q.type === "LIKERT_COMPOSITE") {
          // 展开复合题：将每个子题答案直接写入后端答案（复合题父 ID 本身不发送）
          const compositeAnswer = frontendAnswers[q.id];
          if (compositeAnswer && typeof compositeAnswer === "object") {
            Object.keys(compositeAnswer).forEach((subQuestionId) => {
              backendAnswers[subQuestionId] = compositeAnswer[subQuestionId];
            });
          }
        } else {
          backendAnswers[q.id] = frontendAnswers[q.id];
        }
      });

      return backendAnswers;
    },

    handleSubmitClick() {
      if (!this.canSubmit) {
        // 清除之前的错误标记
        this.questionErrors.clear();

        // 获取可见题目ID集合（排除被跳转逻辑跳过的题目）
        const visibleQuestionIds = new Set(
          this.visibleQuestions.map((q) => q.id)
        );

        // 找到所有未填写的必填题（只检查可见的题目）
        const uncompletedQuestions = this.viewQuestions
          .filter((q) => q.required && visibleQuestionIds.has(q.id)) // 只验证可见的必填题
          .filter((q) => {
            // Likert复合题
            if (q.type === "LIKERT_COMPOSITE") {
              const compositeAnswers = this.answers[q.id];
              if (!compositeAnswers || typeof compositeAnswers !== "object") {
                return true;
              }
              return !q.compositeConfig.likert.subQuestions.every((subQ) => {
                const subAnswer = compositeAnswers[subQ.id];
                return subAnswer !== null && subAnswer !== undefined;
              });
            }

            // 矩阵题
            if (q.type === "MATRIX") {
              const matrixAnswer = this.answers[q.id];
              if (
                !matrixAnswer ||
                typeof matrixAnswer !== "object" ||
                Object.keys(matrixAnswer).length === 0
              ) {
                return true;
              }
              return !q.matrixConfig?.rows?.every((row) => {
                return (
                  matrixAnswer[row.id] !== null &&
                  matrixAnswer[row.id] !== undefined
                );
              });
            }

            // 单选题：检查"其他"选项的自定义输入
            if (q.type === "SINGLE_CHOICE") {
              const answer = this.answers[q.id];
              if (answer === null || answer === undefined) return true;
              if (typeof answer === "string" && answer.trim() === "")
                return true;

              // 检查是否有"其他"选项（allowCustomInput=true）
              const otherOption = q.options?.find(
                (opt) => opt.allowCustomInput === true
              );
              if (otherOption) {
                // 如果选择了"其他"选项
                const answerStr = String(answer);
                const selectedValue = answerStr.includes("|")
                  ? answerStr.split("|")[0]
                  : answerStr;
                if (selectedValue === otherOption.value) {
                  // 必须填写自定义内容（答案格式必须是"选项值|自定义文本"）
                  if (!answerStr.includes("|")) {
                    return true; // 未填写自定义内容
                  }
                  const customText = answerStr.split("|")[1];
                  if (!customText || customText.trim() === "") {
                    return true; // 自定义内容为空
                  }
                }
              }
              return false;
            }

            // 其他题型
            const answer = this.answers[q.id];
            if (answer === null || answer === undefined) return true;
            if (typeof answer === "string" && answer.trim() === "") return true;
            if (Array.isArray(answer) && answer.length === 0) return true;
            return false;
          });

        if (uncompletedQuestions.length > 0) {
          // 标记所有未填写的题目为错误状态
          uncompletedQuestions.forEach((q) => {
            this.questionErrors.add(q.id);
          });

          // 找到第一个未填写的题目并滚动到它
          const firstUncompleted = uncompletedQuestions[0];
          this.scrollToQuestion(firstUncompleted.id);
        }
        return;
      }

      // 清除错误标记
      this.questionErrors.clear();
      this.submitQuestionnaire();
    },

    // 处理跳转逻辑
    handleSkipLogic(newAnswers, oldAnswers) {
      if (!this.sortedViewQuestions || this.sortedViewQuestions.length === 0) {
        return;
      }

      // 找出答案发生变化的题目
      const changedQuestions = this.sortedViewQuestions.filter((q) => {
        const newAnswer = newAnswers[q.id];
        const oldAnswer = oldAnswers?.[q.id];
        return (
          newAnswer !== oldAnswer &&
          newAnswer !== null &&
          newAnswer !== undefined
        );
      });

      // 检查每个变化题目的跳转逻辑
      for (const question of changedQuestions) {
        const skipLogic = question.skipLogic;
        if (!skipLogic) continue;

        const answer = newAnswers[question.id];
        const answerValue = String(answer);
        let shouldSkip = false;

        if (skipLogic.condition === "EQUALS") {
          shouldSkip = answerValue === skipLogic.conditionValue;
        } else if (skipLogic.condition === "NOT_EQUALS") {
          shouldSkip = answerValue !== skipLogic.conditionValue;
        }

        if (shouldSkip) {
          // 如果跳过的题目有答案，清空它
          if (skipLogic.targetQuestionId) {
            // 跳转到指定题目，清空跳过的题目答案
            const skippedQuestionIds = this.getSkippedQuestionIds(
              question.id,
              skipLogic.targetQuestionId
            );
            skippedQuestionIds.forEach((skippedId) => {
              // 清空被跳过题目的答案
              const skippedQuestion = this.sortedViewQuestions.find(
                (q) => q.id === skippedId
              );
              if (skippedQuestion) {
                if (skippedQuestion.type === "MULTIPLE_CHOICE") {
                  this.answers[skippedId] = [];
                } else if (skippedQuestion.type === "MATRIX") {
                  this.answers[skippedId] = {};
                } else {
                  this.answers[skippedId] = null;
                }
              }
            });

            // 滚动到目标题目
            setTimeout(() => {
              this.scrollToQuestion(skipLogic.targetQuestionId);
            }, 300); // 延迟300ms，等待DOM更新
          } else {
            // 跳过下一题，清空下一题的答案
            const currentIndex = this.sortedViewQuestions.findIndex(
              (q) => q.id === question.id
            );
            if (
              currentIndex >= 0 &&
              currentIndex + 1 < this.sortedViewQuestions.length
            ) {
              const nextQuestion = this.sortedViewQuestions[currentIndex + 1];
              if (nextQuestion.type === "MULTIPLE_CHOICE") {
                this.answers[nextQuestion.id] = [];
              } else if (nextQuestion.type === "MATRIX") {
                this.answers[nextQuestion.id] = {};
              } else {
                this.answers[nextQuestion.id] = null;
              }
            }
          }
        }
      }
    },

    // 获取被跳过的题目ID列表（从当前题目到目标题目之间的所有题目）
    getSkippedQuestionIds(currentQuestionId, targetQuestionId) {
      const currentIndex = this.sortedViewQuestions.findIndex(
        (q) => q.id === currentQuestionId
      );
      const targetIndex = this.sortedViewQuestions.findIndex(
        (q) => q.id === targetQuestionId
      );

      if (currentIndex < 0 || targetIndex < 0 || targetIndex <= currentIndex) {
        return [];
      }

      const skippedIds = [];
      for (let i = currentIndex + 1; i < targetIndex; i++) {
        skippedIds.push(this.sortedViewQuestions[i].id);
      }
      return skippedIds;
    },

    // 设置题目组件的 ref
    setQuestionRef(questionId, el) {
      if (el) {
        this.questionRefs.set(questionId, el);
      } else {
        this.questionRefs.delete(questionId);
      }
    },

    // 滚动到指定题目
    scrollToQuestion(questionId) {
      // 锁定滚动检测，避免滚动过程中误判
      this.scrollCheckLocked = true;
      // 强制设置为非底部状态，确保按钮保持悬浮
      this.isScrolledToBottom = false;

      // 强制更新，确保按钮状态立即反映到DOM
      this.$forceUpdate();

      this.$nextTick(() => {
        // 方案1：尝试使用 ref
        const questionRef = this.questionRefs.get(questionId);
        if (questionRef && questionRef.$el) {
          const element = questionRef.$el;
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // 滚动动画完成后（smooth 滚动通常需要 500-1000ms），解锁
          setTimeout(() => {
            // 添加一个短暂的闪烁效果，突出显示错误题目
            if (this.questionErrors.has(questionId)) {
              // 错误状态会通过 has-error class 保持显示
            }

            // 延迟解锁，确保滚动动画完全结束
            // 注意：不重新检查滚动位置，保持按钮悬浮状态，直到用户手动滚动到底部
            setTimeout(() => {
              this.scrollCheckLocked = false;
              // 不重新检查滚动位置，让用户手动滚动到底部时再检查
              // 这样可以确保按钮保持悬浮状态
            }, 200);
          }, 800);
          return;
        }

        // 方案2：尝试通过题目序号查找（更可靠）
        const question = this.viewQuestions.find((q) => q.id === questionId);
        if (question) {
          // 提取题目序号（从ID中提取，如 q24 -> 24）
          const questionNumberMatch = questionId.match(/q(\d+)/);
          const questionNumber = questionNumberMatch
            ? questionNumberMatch[1]
            : null;

          if (questionNumber) {
            // 通过题目序号查找（更精确）
            const numberElements = Array.from(
              document.querySelectorAll(".question-number")
            );
            const targetNumberElement = numberElements.find((el) => {
              const text = el.textContent?.trim();
              return (
                text === `${questionNumber}.` || text === `${questionNumber}。`
              );
            });

            if (targetNumberElement) {
              const parentElement = targetNumberElement.closest(
                ".questionnaire-item, .question-item"
              );
              if (parentElement) {
                parentElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                parentElement.classList.add("error-highlight");
                setTimeout(() => {
                  parentElement.classList.remove("error-highlight");
                }, 2000);

                // 滚动动画完成后解锁
                setTimeout(() => {
                  setTimeout(() => {
                    this.scrollCheckLocked = false;
                  }, 200);
                }, 800);
                return;
              }
            }
          }

          // 方案3：通过题目标题查找（降级方案）
          console.warn(
            `[问卷调试] 无法找到题目 ${questionId} 的 ref，尝试使用 DOM 查询`
          );
          const titleElement = Array.from(
            document.querySelectorAll(
              ".question-title, .likert-question-header"
            )
          ).find((el) => {
            const text = el.textContent || "";
            const questionTitle = question.title || "";
            // 匹配题目标题的前30个字符（避免标题过长导致匹配失败）
            return text.includes(questionTitle.substring(0, 30));
          });

          if (titleElement) {
            titleElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            const parentElement = titleElement.closest(
              ".questionnaire-item, .question-item"
            );
            if (parentElement) {
              parentElement.classList.add("error-highlight");
              setTimeout(() => {
                parentElement.classList.remove("error-highlight");
              }, 2000);
            }

            // 滚动动画完成后解锁
            setTimeout(() => {
              setTimeout(() => {
                this.scrollCheckLocked = false;
              }, 200);
            }, 800);
            return;
          }
        }

        // 如果所有方案都失败，立即解锁
        console.error(
          `[问卷调试] 无法找到题目 ${questionId} 的DOM元素，无法滚动`
        );
        setTimeout(() => {
          this.scrollCheckLocked = false;
        }, 100);
      });
    },

    // 防抖的滚动检测
    debouncedCheckScrollPosition() {
      // 如果检测被锁定，跳过本次检测
      if (this.scrollCheckLocked) {
        return;
      }

      // 清除之前的定时器
      if (this.scrollCheckTimer) {
        clearTimeout(this.scrollCheckTimer);
      }
      // 防抖：200ms 后执行检测
      this.scrollCheckTimer = setTimeout(() => {
        this.checkScrollPosition();
      }, 200);
    },

    // 检查滚动位置
    checkScrollPosition() {
      // 如果检测被锁定，跳过本次检测
      if (this.scrollCheckLocked) {
        return;
      }

      requestAnimationFrame(() => {
        const scrollTop =
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          document.body.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        );

        // 判断是否到达底部（增加误差范围，避免抖动）
        // 使用更大的误差范围（80px），避免在边界处频繁切换
        const isAtBottom = scrollTop + windowHeight >= documentHeight - 80;
        // 如果页面内容高度小于等于窗口高度的1.15倍，认为已经在底部
        const isContentShort =
          documentHeight > 0 && documentHeight <= windowHeight * 1.15;
        const newIsScrolledToBottom = isAtBottom || isContentShort;

        // 只有状态改变时才更新，避免不必要的响应式更新
        if (this.isScrolledToBottom !== newIsScrolledToBottom) {
          // 锁定检测，防止按钮切换时触发新的检测
          this.scrollCheckLocked = true;
          this.isScrolledToBottom = newIsScrolledToBottom;

          // 300ms 后解锁，允许再次检测
          setTimeout(() => {
            this.scrollCheckLocked = false;
          }, 300);
        }
      });
    },

    // 清除已填写题目的错误状态
    clearErrorsForAnsweredQuestions(newAnswers) {
      this.questionErrors.forEach((questionId) => {
        const question = this.viewQuestions.find((q) => q.id === questionId);
        if (!question || !question.required) {
          return;
        }

        let isAnswered = false;

        if (question.type === "LIKERT_COMPOSITE") {
          const compositeAnswers = newAnswers[questionId];
          if (compositeAnswers && typeof compositeAnswers === "object") {
            isAnswered = question.compositeConfig.likert.subQuestions.every(
              (subQ) => {
                const subAnswer = compositeAnswers[subQ.id];
                return subAnswer !== null && subAnswer !== undefined;
              }
            );
          }
        } else if (question.type === "MATRIX") {
          const matrixAnswer = newAnswers[questionId];
          if (
            matrixAnswer &&
            typeof matrixAnswer === "object" &&
            Object.keys(matrixAnswer).length > 0
          ) {
            isAnswered = question.matrixConfig?.rows?.every((row) => {
              return (
                matrixAnswer[row.id] !== null &&
                matrixAnswer[row.id] !== undefined
              );
            });
          }
        } else if (question.type === "SINGLE_CHOICE") {
          // 单选题：检查"其他"选项的自定义输入
          const answer = newAnswers[questionId];
          if (answer !== null && answer !== undefined) {
            const answerStr = String(answer);
            if (answerStr.trim() !== "") {
              // 检查是否有"其他"选项（allowCustomInput=true）
              const otherOption = question.options?.find(
                (opt) => opt.allowCustomInput === true
              );
              if (otherOption) {
                // 如果选择了"其他"选项
                const selectedValue = answerStr.includes("|")
                  ? answerStr.split("|")[0]
                  : answerStr;
                if (selectedValue === otherOption.value) {
                  // 必须填写自定义内容（答案格式必须是"选项值|自定义文本"）
                  if (answerStr.includes("|")) {
                    const customText = answerStr.split("|")[1];
                    if (customText && customText.trim() !== "") {
                      isAnswered = true;
                    }
                  }
                  // 如果未填写自定义内容，isAnswered 保持为 false
                } else {
                  // 选择了非"其他"选项，视为已填写
                  isAnswered = true;
                }
              } else {
                // 没有"其他"选项，按普通单选题处理
                isAnswered = true;
              }
            }
          }
        } else {
          const answer = newAnswers[questionId];
          if (answer !== null && answer !== undefined) {
            if (typeof answer === "string" && answer.trim() !== "") {
              isAnswered = true;
            } else if (Array.isArray(answer) && answer.length > 0) {
              isAnswered = true;
            } else if (
              typeof answer === "number" ||
              typeof answer === "object"
            ) {
              isAnswered = true;
            }
          }
        }

        // 如果已填写，清除错误状态
        if (isAnswered) {
          this.questionErrors.delete(questionId);
        }
      });
    },

    async submitQuestionnaire() {
      this.submitting = true;
      try {
        // 转换答案格式：Likert复合题的答案需要展开为子问题答案
        const backendAnswers = this.transformAnswersForBackend(this.answers);

        const res = await post(
          `/api/my/exam/${this.examId}/questionnaire/submit`,
          { answers: backendAnswers },
          { timeout: 60000 }
        );

        if (res.success) {
          // 提交成功后清除缓存
          this.clearAnswersCache();
          this.$toast.success("问卷提交成功");
          this.$emit(
            "submit-success",
            res.data?.examResult || res.examResult || res.data || res
          );
        } else {
          this.$toast.fail(res.message || "问卷提交失败");
        }
      } catch (error) {
        console.error("提交问卷失败:", error);
        const errorMessage = error.message || "问卷提交失败，请稍后重试";

        // 尝试从错误消息中提取题目序号和标题，并滚动到该题目
        // 错误消息格式：第X题「xxx」为必填项，请填写
        const errorMatch = errorMessage.match(/第(\d+)题「([^」]+)」/);
        if (errorMatch) {
          const questionNumber = errorMatch[1];
          const questionTitle = errorMatch[2];
          // 显示友好的错误提示
          this.$toast.fail(`第${questionNumber}题，${questionTitle}没有填写`);

          // 查找匹配的题目
          const question = this.viewQuestions.find(
            (q) => q.title && q.title.includes(questionTitle)
          );
          if (question) {
            // 标记为错误状态
            this.questionErrors.add(question.id);
            // 滚动到该题目
            this.scrollToQuestion(question.id);
          }
        } else {
          // 降级方案：尝试旧格式（兼容性处理）
          const titleMatch = errorMessage.match(/题目「([^」]+)」/);
          if (titleMatch) {
            const questionTitle = titleMatch[1];
            // 查找匹配的题目
            const question = this.viewQuestions.find(
              (q) => q.title && q.title.includes(questionTitle)
            );
            if (question) {
              // 从题目ID提取序号
              const match = question.id.match(/q(\d+)/);
              const questionNumber = match && match[1] ? match[1] : "?";
              this.$toast.fail(
                `第${questionNumber}题，${questionTitle}没有填写`
              );
              // 标记为错误状态
              this.questionErrors.add(question.id);
              // 滚动到该题目
              this.scrollToQuestion(question.id);
            }
          } else {
            // 无法解析错误消息，显示原始错误
            this.$toast.fail(errorMessage);
          }
        }
      } finally {
        this.submitting = false;
      }
    },

    /**
     * 从 localStorage 恢复已保存的答案（断点续填）
     */
    restoreAnswersFromCache() {
      if (!this.cacheKey) {
        return;
      }

      try {
        const cachedAnswers = getStorage(this.cacheKey, "local");
        if (cachedAnswers && typeof cachedAnswers === "object") {
          // 合并缓存的答案到当前 answers（只恢复已初始化的题目）
          Object.keys(cachedAnswers).forEach((questionId) => {
            if (this.answers.hasOwnProperty(questionId)) {
              // 对于 Likert 复合题，需要深度合并
              if (
                this.viewQuestions.some(
                  (q) => q.id === questionId && q.type === "LIKERT_COMPOSITE"
                )
              ) {
                // Likert 复合题的答案是一个对象，需要深度合并
                if (
                  typeof cachedAnswers[questionId] === "object" &&
                  cachedAnswers[questionId] !== null
                ) {
                  this.answers[questionId] = {
                    ...this.answers[questionId],
                    ...cachedAnswers[questionId],
                  };
                }
              } else {
                // 其他题型直接赋值
                this.answers[questionId] = cachedAnswers[questionId];
              }
            }
          });

          console.log("[问卷调试] 从缓存恢复答案:", {
            cacheKey: this.cacheKey,
            restoredCount: Object.keys(cachedAnswers).length,
          });
        }
      } catch (error) {
        console.warn("[问卷调试] 恢复缓存答案失败:", error);
      }
    },

    /**
     * 保存答案到 localStorage（防抖处理，避免频繁写入）
     */
    saveAnswersToCache(answers) {
      if (!this.cacheKey || !answers) {
        return;
      }

      // 清除之前的定时器
      if (this.saveCacheTimer) {
        clearTimeout(this.saveCacheTimer);
      }

      // 防抖：500ms 后保存
      this.saveCacheTimer = setTimeout(() => {
        try {
          setStorage({ key: this.cacheKey, value: answers }, "local");
          console.log("[问卷调试] 答案已保存到缓存:", this.cacheKey);
        } catch (error) {
          console.warn("[问卷调试] 保存答案到缓存失败:", error);
        }
      }, 500);
    },

    /**
     * 清除答案缓存
     */
    clearAnswersCache() {
      if (!this.cacheKey) {
        return;
      }

      try {
        removeStorage([this.cacheKey], "local");
        console.log("[问卷调试] 已清除答案缓存:", this.cacheKey);
      } catch (error) {
        console.warn("[问卷调试] 清除答案缓存失败:", error);
      }
    },
  },
};
</script>

<style lang="less" scoped>
.mobile-questionnaire-page {
  min-height: 100vh;
  background-color: #f5f5f5;

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 400px;
  }

  .questionnaire-content {
    padding: 20px 16px;
    background-color: #f5f5f5;
    padding-bottom: 30px;
    // 当按钮是 fixed-bottom 时，增加底部 padding，避免按钮遮挡内容
    &.has-fixed-button {
      padding-bottom: 80px;
    }

    .questionnaire-header {
      margin-bottom: 24px;
      padding: 0;
      background-color: #fff;
      padding: 20px 16px;
      border-radius: 0;

      h2 {
        margin: 0 0 20px 0;
        padding: 0;
        font-size: 24px;
        font-weight: 600;
        color: #0095ff;
        text-align: center;
        line-height: 1.5;
      }

      .questionnaire-warning {
        display: flex;
        align-items: center;
        background: linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%);
        border: 2px solid #ffc107;
        border-radius: 8px;
        padding: 14px 16px;
        margin: 16px 0;
        box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);

        .warning-icon {
          font-size: 28px;
          color: #ff9800;
          margin-right: 10px;
          flex-shrink: 0;
          font-weight: bold;
          line-height: 1;
        }

        .warning-text {
          font-size: 15px;
          font-weight: 600;
          color: #e65100;
          line-height: 1.5;
          flex: 1;
        }
      }

      .questionnaire-intro {
        text-align: left;
        line-height: 2.2;
        color: #333;
        font-size: 16px;
        margin-top: 16px;

        p {
          margin: 0 0 12px 0;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }

    .questionnaire-questions {
      margin-bottom: 30px;
    }

    .submit-section {
      margin-top: 20px;
      padding: 0;

      &.relative-position {
        position: relative !important;
        margin-top: 20px !important;
      }

      .submit-btn-inner {
        height: 44px;
        font-size: 16px;
        border-radius: 4px;

        &.has-errors {
          border: 2px solid #ff4d4f !important;
        }
      }
    }
  }

  // 固定定位按钮样式（在 .questionnaire-content 外部）
  .submit-section.fixed-bottom {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    padding: 12px 16px !important;
    background-color: #fff !important;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1) !important;
    z-index: 1000 !important;
    margin: 0 !important;

    .submit-btn-inner {
      height: 44px;
      font-size: 16px;
      border-radius: 4px;

      &.has-errors {
        border: 2px solid #ff4d4f !important;
      }
    }
  }

  // 错误高亮动画
  @keyframes error-highlight {
    0%,
    100% {
      background-color: transparent;
    }
    50% {
      background-color: rgba(255, 77, 79, 0.1);
    }
  }

  :deep(.has-error) {
    animation: error-highlight 0.5s ease-in-out;
    border-left: 3px solid #ff4d4f !important;
    padding-left: 12px !important;

    &::after {
      content: "请填写此项";
      display: block;
      color: #ff4d4f;
      font-size: 12px;
      margin-top: 4px;
    }
  }
}
</style>
